import React, { type FormEvent, type ReactNode, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  ERROR_MESSAGE_SHOWN,
  CONTINUE_ERROR_SHOWN,
  RECURRING_ERROR_SHOWN,
  FORM_VALIDATION_ERROR,
} from 'src/app/constants/errorMessages';
import { formStatus } from 'src/app/constants/formStatus';
import {
  DEFAULT_TIMEOUT_JOB_SAVING,
  DEFAULT_TIMEOUT_JOB_SAVED,
} from 'src/app/constants/timeouts';
import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendNextButtonEvent, sendLinkEvent } from 'src/app/utils/analytics';
import { isHorizonHirer } from 'src/app/utils/isHorizonHirer';
import parseValidationResult from 'src/app/utils/parseValidationResult';
import { withDispatchDraft } from 'src/app/views/enhancers/withDispatchDraft';
import type AppState from 'src/types/appState';
import type { DispatchDraftResponse } from 'src/types/draft';
import type {
  ValidationComponent,
  ValidationResultType,
} from 'src/types/validation';

export interface FormProps extends Partial<ValidationComponent> {
  children?: ReactNode;
  handleDispatchDraft?: (isCompleted?: boolean) => DispatchDraftResponse;
  handleNextPageRoute: () => void;
}

export const Form = (props: any) => {
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState(formStatus.EDIT);
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const { pricingModel, tealium } = useSelector(
    (state: AppState) => ({
      pricingModel: state.session.hirerAccount?.pricingModel ?? undefined,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const handleSavedState = () => {
    setStatus(formStatus.SAVED);
    setTimeout(() => {
      props.handleNextPageRoute();
    }, DEFAULT_TIMEOUT_JOB_SAVED);
  };

  const handleValidationResult = (
    validationResult: ValidationResultType,
  ): void => {
    const { isValid, errorMessages } = parseValidationResult(validationResult);

    if (!isValid) {
      if (isPageAnalyticsEnabled) {
        const eventData = {
          eventName: FORM_VALIDATION_ERROR,
          errorMessage: errorMessages.join(','),
          validationType: 'form',
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        const eventData = {
          linkName: ERROR_MESSAGE_SHOWN,
          eventName: [ERROR_MESSAGE_SHOWN, CONTINUE_ERROR_SHOWN],
          errorMessage: errorMessages.join(','),
        };
        if (count >= 1) {
          eventData.eventName.push(RECURRING_ERROR_SHOWN);
        }
        sendLinkEvent(tealium, eventData);
      }
      setCount((prevCount) => prevCount + 1);
    } else {
      setCount(0);
      props.handleDispatchDraft?.();
      setStatus(formStatus.SAVING);
      setTimeout(() => {
        handleSavedState();
      }, DEFAULT_TIMEOUT_JOB_SAVING);

      if (isPageAnalyticsEnabled) {
        const eventPageName = tealium.page.replace('-', '_');

        const eventData = {
          eventName: `${eventPageName}${'_continue_pressed'}`,
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        sendNextButtonEvent(tealium, isHorizonHirer(pricingModel));
      }
    }
  };

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const { handleValidation } = props;
    const isEdit = status === formStatus.EDIT;

    if (isEdit) {
      const validationResult = !handleValidation || (await handleValidation());
      return handleValidationResult(validationResult);
    }
  };

  return <form onSubmit={handleSubmit}>{props.children}</form>;
};

export default withDispatchDraft(Form);
